// `GET /api/v1/audit`: the audit log under the reader's context (Phase 3 · D11, #637).
//
// Read off `control_plane/routes/audit.py` and `audit/read.py`. A workspace's read
// answers its own rows; an org's answers the rows carrying its `org_id` and no
// workspace. The event catalogue is the old estate's, kept verbatim
// (`auth/audit.py::EVENTS`), so a SIEM rule written there matches here.
//
// Each row also carries how a person reads it (#812): the actor as the route
// resolved it for this context (`audit/actors.py`), with the stored actor kept
// beside it, and the action's label and category (`auth/audit.py::EVENT_LABELS`).
//
// The read is filtered and paged on the server (#813): a page is `{items, next_cursor}`,
// and the next page is asked for by that cursor with the same filter. The CSV of a
// filtered range, and the actors the filter offers, are routes of their own, gated as
// the read is.

import { browserApi, type Api } from "./http";

export const AUDIT_PATH = "/api/v1/audit";
export const AUDIT_ACTORS_PATH = "/api/v1/audit/actors";
export const AUDIT_CSV_PATH = "/api/v1/audit.csv";

/** Who acted, as the route names them to this context; `kind` says which rule named them. */
export interface ActorDisplay {
  kind: string;
  name: string;
  email: string | null;
}

export interface AuditRow {
  id: string;
  occurred_at: string;
  workspace_id: string | null;
  org_id: string | null;
  actor: string;
  actor_display: ActorDisplay | null;
  action: string;
  action_label: string | null;
  action_category: string | null;
  target: string | null;
  detail: Record<string, unknown>;
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

function actorFrom(raw: unknown): ActorDisplay | null {
  const value = record(raw);
  const kind = text(value.kind);
  const name = text(value.name);
  if (kind === null || name === null) return null;
  return { kind, name, email: text(value.email) };
}

/** One row as the route answers it, or null for a shape the route does not serve. */
function rowFrom(raw: unknown): AuditRow | null {
  const row = record(raw);
  const id = text(row.id);
  const occurred = text(row.occurred_at);
  const actor = text(row.actor);
  const action = text(row.action);
  if (id === null || occurred === null || actor === null || action === null) return null;
  return {
    id,
    occurred_at: occurred,
    workspace_id: text(row.workspace_id),
    org_id: text(row.org_id),
    actor,
    actor_display: actorFrom(row.actor_display),
    action,
    action_label: text(row.action_label),
    action_category: text(row.action_category),
    target: text(row.target),
    detail: record(row.detail),
  };
}

/** The route's answer as rows, dropping anything that is not one rather than crashing. */
export function auditRowsFrom(raw: unknown): AuditRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(rowFrom).filter((row): row is AuditRow => row !== null);
}

/** One page of the log and the cursor to the next, or null at the end. */
export interface AuditPage {
  items: AuditRow[];
  next_cursor: string | null;
}

/** The page as the route answers it; anything else is an empty last page. */
export function auditPageFrom(raw: unknown): AuditPage {
  const page = record(raw);
  return { items: auditRowsFrom(page.items), next_cursor: text(page.next_cursor) };
}

/**
 * What the log is narrowed to, each field empty for "any". `from` and `to` are the
 * dates a person picks (`YYYY-MM-DD`), both days included, read in their own time zone.
 */
export interface AuditFilter {
  /** Stored actors, any of them: the screen offers everyone it names alike as one choice. */
  actors: string[];
  category: string;
  from: string;
  to: string;
}

export const NO_FILTER: AuditFilter = { actors: [], category: "", from: "", to: "" };

/** Local midnight at the start of a picked date, `days` later, as an instant; null if unparsable. */
function startOfDay(date: string, days = 0): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return null;
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days);
  return Number.isNaN(day.getTime()) ? null : day.toISOString();
}

/** The filter as the route's query: `since` inclusive, `until` the start of the day after `to`. */
export function auditQuery(filter: AuditFilter): URLSearchParams {
  const query = new URLSearchParams();
  for (const actor of filter.actors) query.append("actor", actor);
  if (filter.category !== "") query.set("category", filter.category);
  const since = startOfDay(filter.from);
  if (since !== null) query.set("since", since);
  const until = startOfDay(filter.to, 1);
  if (until !== null) query.set("until", until);
  return query;
}

const withQuery = (path: string, query: URLSearchParams): string => {
  const text = query.toString();
  return text === "" ? path : `${path}?${text}`;
};

export function auditPath(filter: AuditFilter = NO_FILTER, cursor: string | null = null): string {
  const query = auditQuery(filter);
  if (cursor !== null) query.set("cursor", cursor);
  return withQuery(AUDIT_PATH, query);
}

/** Where the CSV of the filtered range downloads from. */
export const auditCsvPath = (filter: AuditFilter = NO_FILTER): string =>
  withQuery(AUDIT_CSV_PATH, auditQuery(filter));

export const readAudit = (
  api: Api = browserApi,
  filter: AuditFilter = NO_FILTER,
  cursor: string | null = null,
): Promise<AuditPage> => api.get<unknown>(auditPath(filter, cursor)).then(auditPageFrom);

/** An actor the filter can offer, named as the log names it. */
export interface AuditActor {
  actor: string;
  actor_display: ActorDisplay | null;
}

export function auditActorsFrom(raw: unknown): AuditActor[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const value = record(item);
    const actor = text(value.actor);
    return actor === null ? [] : [{ actor, actor_display: actorFrom(value.actor_display) }];
  });
}

export const readAuditActors = (api: Api = browserApi): Promise<AuditActor[]> =>
  api.get<unknown>(AUDIT_ACTORS_PATH).then(auditActorsFrom);
